import React, { useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import TableWithPagination from "../TableWithPagination";
import SvgIcon from "../SvgIcon";

import { MatchContext, SearchContext } from "context/providers";
import NumericCircle from "components/NumericCircle";

import styles from "components/index.module.css";
import { cn } from "utils";

const Tabs = ({
  items,
  className,
  headerClassName,
  initialIndex = 0,
  updateQs,
  rootDataTestId,
}) => {
  const [activeTab, setActiveTab] = useState(items[initialIndex]);
  const { ideal } = useContext(MatchContext);
  const { setSearchState, searchState } = useContext(SearchContext);

  const renderTabs = () => {
    return (
      <div
        className={cn(
          "flex flex-wrap justify-start lg:justify-around w-full pt-2 gap-x-6 px-2 lg:px-0",
          headerClassName
        )}
      >
        {items.map((item, index) => {
          const tabTitle = item.title;
          const isTabActive = activeTab.title === tabTitle;
          const isIdeal = tabTitle === "Ideal Fit";

          return (
            <div
              id={`tab-${item.uniqueId}`}
              className="tab flex gap-x items-center"
              key={`${tabTitle}_${index}`}
            >
              <div
                data-testid={`tab-${item.uniqueId}-btn`}
                aria-selected={isTabActive}
                onClick={() => {
                  if (updateQs) {
                    updateQs(item.uniqueId);
                  }
                  if (!isTabActive) {
                    setActiveTab(item);
                    if (searchState?.basicSearch) {
                      setSearchState({ ...searchState, basicSearch: false });
                    }
                  }
                }}
                className={cn(
                  "whitespace-nowrap flex items-center text-text-grey-900 font-medium text-xs sm:text-sm lg:text-md  mx-2 lg:mx-2.5 pb-2 hover:fill-sky-500 hover:text-sky-500 transition-all cursor-pointer relative -bottom-[2px] border-b-2 border-[transparent]",
                  {
                    "text-sky-500 border-b-2 !border-sky-500": isTabActive,
                  }
                )}
              >
                {item.icon && (
                  <SvgIcon
                    type={item.icon}
                    className={cn("mr-2", {
                      "fill-sky-500": isTabActive,
                    })}
                  />
                )}

                <p>{tabTitle}</p>
              </div>

              {!isIdeal && item.count > 0 && (
                <NumericCircle
                  value={item.hasMore ? `${item.count} +` : item.count}
                  isActive={isTabActive}
                  dataTestId={`tab-${item.uniqueId}-counter`}
                />
              )}
              {isIdeal && ideal.length > 0 && (
                <NumericCircle
                  value={ideal.length}
                  isActive={isTabActive}
                  dataTestId={`tab-${item.uniqueId}-counter`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = useCallback(() => {
    switch (activeTab?.type) {
      case "table": {
        return (
          <div className={cn("overflow-auto", styles.customScrollBar)}>
            <TableWithPagination {...activeTab} />
          </div>
        );
      }

      case "text": {
        return <p>{activeTab.content}</p>;
      }

      case "custom": {
        return activeTab.renderCustomContent(activeTab);
      }

      default: {
        return <></>;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  if (!items.length) {
    return null;
  }

  return (
    <div
      className={cn("flex flex-col", className)}
      data-testid={rootDataTestId}
    >
      <div className="border-b-2 border-text-grey-900 sticky top-0 bg-white z-10 py-2">
        {renderTabs()}
      </div>
      {renderContent()}
    </div>
  );
};

Tabs.propTypes = {
  items: PropTypes.array.isRequired,
  className: PropTypes.string,
  rootDataTestId: PropTypes.string,
};

Tabs.defaultProps = {
  className: "",
};

export default Tabs;
